/* =============================================================
   useArchive.js — Archived students with term grouping
   -------------------------------------------------------------
   Fetches all archived students and exposes:
     terms            — distinct { semesterId, name, incoming, outgoing }
                        sorted alphabetically (Unassigned last)
     students         — raw archived student rows (all terms)
     loading / error
     restoreTerm(semesterId) — sets archived=false for all students
                               in that term → they return to active lists
                               (pass null for "Unassigned" students)
     deleteTerm(semesterId)  — permanently deletes all students in that
                               term from the DB (irreversible)

   Usage:
     const { terms, students, loading, error,
             restoreTerm, deleteTerm } = useArchive();
   ============================================================= */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';

const FETCH_TIMEOUT_MS = 8000;

export function useArchive() {
  const { user } = useAuth();

  const [students, setStudents] = useState([]);
  const [terms,    setTerms]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!user?.id) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    const timeoutId  = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setError(null);

    try {
      const { data, error: e } = await supabase
        .from('students')
        .select('id, name, email, type, country, nationality, exchange_type, eu_status, semester_id, home_university, destination_university, partner_universities(name), semesters(name)')
        .eq('archived', true)
        .order('name', { ascending: true })
        .abortSignal(signal);

      if (signal.aborted) return;
      if (e) throw e;

      const rows = data ?? [];
      setStudents(rows);

      // Build distinct term list grouped by semester_id
      const map = {};
      for (const s of rows) {
        const termId   = s.semester_id ?? null;
        const termName = s.semesters?.name ?? 'Unassigned';
        const key      = termId ?? '__unassigned__';
        if (!map[key]) map[key] = { semesterId: termId, name: termName, incoming: 0, outgoing: 0 };
        if (s.type === 'incoming') map[key].incoming++;
        if (s.type === 'outgoing') map[key].outgoing++;
      }

      // Sort: named terms alphabetically, Unassigned last
      const sorted = Object.values(map).sort((a, b) => {
        if (a.semesterId === null) return 1;
        if (b.semesterId === null) return -1;
        return a.name.localeCompare(b.name);
      });
      setTerms(sorted);

    } catch (err) {
      if (signal.aborted) return;
      setError(err?.message ?? 'Failed to load archive');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  /* ── Refetch when the user switches back to this tab ─────────── */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [load]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);


  /* ── restoreTerm ─────────────────────────────────────────── */
  const [mutating, setMutating] = useState(null); // term key currently being mutated
  const [mutError, setMutError] = useState(null);

  const restoreTerm = async (semesterId) => {
    const key = semesterId ?? '__unassigned__';
    setMutating(key);
    setMutError(null);
    try {
      // 1. Restore all archived students in this term
      let q = supabase.from('students').update({ archived: false }).eq('archived', true);
      q = semesterId ? q.eq('semester_id', semesterId) : q.is('semester_id', null);
      const { error: studentsErr } = await q;
      if (studentsErr) throw studentsErr;

      // 2. Re-open the semester so it reappears in Settings / AddStudent / etc.
      if (semesterId) {
        const { error: semErr } = await supabase
          .from('semesters')
          .update({ is_closed: false })
          .eq('id', semesterId);
        if (semErr) throw semErr;
      }

      await load();
    } catch (err) {
      setMutError(err?.message ?? 'Failed to restore term');
    } finally {
      setMutating(null);
    }
  };


  /* ── deleteTerm ──────────────────────────────────────────── */
  const deleteTerm = async (semesterId) => {
    const key = semesterId ?? '__unassigned__';
    setMutating(key);
    setMutError(null);
    try {
      let q = supabase.from('students').delete().eq('archived', true);
      q = semesterId ? q.eq('semester_id', semesterId) : q.is('semester_id', null);
      const { error: e } = await q;
      if (e) throw e;
      await load();
    } catch (err) {
      setMutError(err?.message ?? 'Failed to delete term');
    } finally {
      setMutating(null);
    }
  };


  return { students, terms, loading, error, mutating, mutError, restoreTerm, deleteTerm };
}
